/*
 *  This file hrone-snackbar.component.ts is part of HROne Inbox. The intelectual property owned by Uneecops Workplace Solution PVT. LTD.
 *  CopyrightYear: 2025
 *  (C) 2015-2025. All Right reserved. Uneecops Workplace Solution PVT. LTD.
 */

/*
 * Custom snackbar card per the new design system — zero Angular Material / PrimeNG dependency.
 *
 * Not placed in templates by consumers: HroneSnackbarService creates it with createComponent(),
 * moves only the template's root .hrone-snackbar element into its position's fixed .hrone-snackbar-stack
 * in <body> (the host stays detached; max 3 cards on screen, the rest queue),
 * and drives it via setInput('config' | 'leaving'). Every part of the
 * card (icon, title, message, actions, close) is optional and rendered only when provided.
 *
 * Renders its own hrone-snackbar-* class names, styled entirely in hrone-snackbar.component.scss
 * (same standard as hrone-splitbutton / hrone-rating).
 */

import {
  ChangeDetectionStrategy,
  Component,
  ViewEncapsulation,
  computed,
  input,
  output,
} from '@angular/core';
import { NgClass } from '@angular/common';
import {
  IHroneSnackbarAction,
  IHroneSnackbarConfig,
  THroneSnackbarActionType,
  THroneSnackbarVariant,
} from './hrone-snackbar.model';

const VARIANT_ICONS: Record<THroneSnackbarVariant, string> = {
  info: 'fa-solid fa-circle-info',
  success: 'fa-solid fa-circle-check',
  warning: 'fa-duotone fa-solid fa-triangle-exclamation', // duotone layers colored in the scss
  danger: 'fa-duotone fa-solid fa-circle-minus',
  pending: 'fa-solid fa-spinner fa-spin',
};

// Each type gets ONLY its own variant class: btn-link forces its underline + color with !important,
// so it must never be combined with a filled variant. px-0 keeps the link flush with the text above it.
const ACTION_TYPE_CLASSES: Record<THroneSnackbarActionType, string> = {
  underline: 'btn btn-link small px-0',
  primary: 'btn btn-primary small',
  secondary: 'btn btn-secondary small',
  danger: 'btn btn-danger small',
};

/** An action plus its precomputed button classes, so the template doesn't call a function per render. */
interface IHroneSnackbarActionView {
  action: IHroneSnackbarAction;
  classes: string;
}

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector -- hrone-ui-library uses the `hrone` prefix
  selector: 'hrone-snackbar',
  imports: [NgClass],
  templateUrl: './hrone-snackbar.component.html',
  styleUrls: ['./hrone-snackbar.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HroneSnackbarComponent {

  // ── Inputs (set by HroneSnackbarService via ComponentRef.setInput) ────────

  readonly config = input<IHroneSnackbarConfig>({});

  /** True while the exit animation plays, just before the service removes the host. */
  readonly leaving = input<boolean>(false);

  // ── Outputs (subscribed by HroneSnackbarService) ──────────────────────────

  readonly closeClick = output<void>();
  readonly actionClick = output<IHroneSnackbarAction>();

  // ── Derived state ─────────────────────────────────────────────────────────

  readonly position = computed(() => this.config().position ?? 'bottom-right');

  readonly iconClass = computed(() => {
    const { icon, variant } = this.config();
    return icon || (variant ? VARIANT_ICONS[variant] : '');
  });

  readonly containerClasses = computed(() => {
    const { variant, styleClass } = this.config();
    return [variant ? `hrone-snackbar-${variant}` : '', styleClass ?? ''].filter(Boolean);
  });

  readonly hasContent = computed(() => {
    const { title, message, actions } = this.config();
    return !!(title || message || actions?.length);
  });

  readonly closable = computed(() => this.config().closable ?? false);

  readonly actionViews = computed<IHroneSnackbarActionView[]>(() =>
    (this.config().actions ?? []).map((action) => ({
      action,
      classes: ACTION_TYPE_CLASSES[action.type ?? 'underline'] ?? ACTION_TYPE_CLASSES.underline,
    })),
  );

  // danger / warning interrupt screen readers; everything else is announced politely.
  readonly role = computed(() => {
    const variant = this.config().variant;
    return variant === 'danger' || variant === 'warning' ? 'alert' : 'status';
  });
}
